import { colors } from '@constants/colors';
import { ScreenSizes, SwiperScreenSizes } from '@interfaces/index';
import { useTheme } from '@mui/material';
import { useMytaverse } from '@providers/MytaverseProvider';

import { NAVIGATION_ALIGN_HEIGHT_PROPERTY } from './constants';

/**
 * Builds the slider styles: bullet colors for the default mode plus the row
 * layout and the paging arrows used with `horizontalNavigation`.
 *
 * @param paginationGap - Gap in px between the slides and the bullets; unset
 * leaves swiper's own overlay placement.
 * @returns Style objects keyed by the element they belong to.
 */
export const useStyles = (paginationGap?: number) => {
  const { breakpoints } = useTheme();
  const { appTheme } = useMytaverse();

  return {
    swiperWrap: {
      '& .swiper-slide': {
        [breakpoints.down(SwiperScreenSizes.Mobile1)]: {
          display: 'flex',
          justifyContent: 'center',
        },
      },
      '& .swiper-pagination-bullet-active': {
        background: appTheme.mainColor,
      },
      // Swiper parks the bullets `position: absolute` over the bottom of the
      // slides, where a slide's own controls live — a click meant for a bullet
      // lands on one of them. Taking them back into the flow drops them below
      // the slides instead, and swiper still hides them when everything fits.
      ...(paginationGap === undefined
        ? {}
        : {
            '& .swiper-pagination': {
              position: 'static',
              marginTop: `${paginationGap}px`,
            },
          }),
    },
    navigationWrap: {
      display: 'flex',
      alignItems: 'center',
      columnGap: '16px',
      [breakpoints.down(ScreenSizes.Tablet)]: {
        columnGap: '8px',
      },
    },
    swiperContainer: {
      flex: 1,
      minWidth: 0,
    },
    navigationBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: 40,
      height: 40,
      // Lines the arrow up with the centre of the slide's preview. The height
      // arrives as an inline custom property so that this class never has to
      // change: swiper writes its own state classes onto the same element, and
      // a re-render under a new class name would wipe them. Unset — nothing to
      // align with — the fallback cancels the offset and the row's `alignItems`
      // keeps the arrow centred.
      marginTop: `calc(var(${NAVIGATION_ALIGN_HEIGHT_PROPERTY}, 40px) / 2 - 20px)`,
      borderRadius: '50%',
      cursor: 'pointer',
      border: `1px solid ${colors.grey_100}`,
      color: colors.grey_500,
      '& svg': {
        fontSize: 24,
      },
      '&:hover': {
        borderColor: appTheme.mainColor,
        color: appTheme.mainColor,
      },
      // Set by swiper on the first / last slide.
      '&.swiper-button-disabled': {
        opacity: 0.35,
        cursor: 'default',
        pointerEvents: 'none',
      },
      // Set by swiper when all slides fit and there is nothing to scroll.
      '&.swiper-button-lock': {
        display: 'none',
      },
      [breakpoints.down(ScreenSizes.Tablet)]: {
        width: 32,
        height: 32,
        marginTop: `calc(var(${NAVIGATION_ALIGN_HEIGHT_PROPERTY}, 32px) / 2 - 16px)`,
        '& svg': {
          fontSize: 20,
        },
      },
    },
  };
};
